import { useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useErrorHandler } from "react-error-boundary";
import IPutOptions from "interfaces/IPutOptions";
import configuration from "config";

const useFetchWithAbortController = () => {
  const ABORT_ERROR_NAME = "AbortError";
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const errorHandler = useErrorHandler();
  const abortControllerRef = useRef<AbortController | null>(null);

  const runner = useCallback(
    async (url: string, options?: IPutOptions) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const accessToken = await getAccessTokenSilently().catch((error) => {
          if (error.error !== "login_required") {
            return error.error;
          }
        });

        if (!accessToken) {
          const { origin } = window.location;
          if (configuration.autoLoginAtUrls.includes(origin)) {
            await loginWithRedirect();
          }
          return;
        }

        const headers = {
          ...(options?.headers instanceof Array ? options.headers : []),
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        };

        const requestOptions = {
          headers,
          signal: controller.signal,
          ...options,
        };
        try {
          const response = await fetch(url, requestOptions);

          if (!response.ok) {
            throw new Error(`${response.status}`);
          }

          return response;
        } catch (error) {
          if (error instanceof DOMException && error.name === ABORT_ERROR_NAME) {
            return;
          }
          errorHandler(error);
        } finally {
          abortControllerRef.current = null;
        }
      } catch (error: any) {
        errorHandler(error);
      }
    },
    [getAccessTokenSilently, loginWithRedirect, errorHandler]
  );

  const putWithAbortController = async (url: string, options: IPutOptions) => runner(url, options);

  return { putWithAbortController };
};

export default useFetchWithAbortController;
